import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, DatePicker, Input, InputNumber, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { AllocationInput, CustomScheduleLineInput, SchedulePreview } from '@/services/financeRecognition.service';
import { amount } from './prepaidDisplay';

const { Text } = Typography;

type Option = { value: string; label: string };

export type AllocationRow = AllocationInput & { key: string };

const toDate = (v?: string | null) => (v ? dayjs(v) : null);
const fromDate = (d: dayjs.Dayjs | null) => (d ? d.format('YYYY-MM-DD') : null);

/** Sum of exact 2-dp decimals as integer cents (avoids float drift in validation messages). */
function cents(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export function allocationTotals(rows: AllocationInput[]) {
  const pctRows = rows.filter((r) => r.allocation_basis === 'PERCENTAGE');
  const fixedRows = rows.filter((r) => r.allocation_basis === 'FIXED_AMOUNT');
  const pct = pctRows.reduce((s, r) => s + Math.round(Number(r.percentage ?? 0) * 10000), 0) / 10000;
  const fixed = fixedRows.reduce((s, r) => s + cents(r.fixed_amount), 0) / 100;
  return { pct, fixed, pctCount: pctRows.length, fixedCount: fixedRows.length };
}

/**
 * Validation mirrored from the engine for immediate feedback (the server re-validates):
 * one basis per contract; percentages total 100; fixed amounts total the recognizable amount.
 */
export function allocationProblem(
  rows: AllocationInput[],
  recognizable?: string | number | null,
  translate?: (key: string, opts?: Record<string, unknown>) => string
): string | null {
  if (!rows.length) return null;
  const tr = translate ?? ((k: string, o?: Record<string, unknown>) => k);
  const totals = allocationTotals(rows);
  if (totals.pctCount && totals.fixedCount) return tr('useEitherPctOrFixed');
  const undated = rows.filter((r) => !r.effective_from && !r.effective_to);
  if (totals.pctCount && undated.length === rows.length && Math.abs(totals.pct - 100) > 0.00001) {
    return tr('percentagesMustTotal100', { pct: totals.pct });
  }
  if (
    totals.fixedCount &&
    recognizable != null &&
    undated.length === rows.length &&
    cents(recognizable) !== Math.round(totals.fixed * 100)
  ) {
    return tr('fixedAmountsMustEqualRecognizable', {
      fixed: amount(totals.fixed),
      recognizable: amount(recognizable),
    });
  }
  return null;
}

export const AllocationEditor: React.FC<{
  rows: AllocationRow[];
  onChange: (rows: AllocationRow[]) => void;
  expenseOptions: Option[];
  projectOptions: Option[];
  departmentOptions: Option[];
  recognizable?: string | number | null;
  disabled?: boolean;
}> = ({ rows, onChange, expenseOptions, projectOptions, departmentOptions, recognizable, disabled }) => {
  const { t } = useTranslation('finance');
  const update = (key: string, patch: Partial<AllocationRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const problem = useMemo(() => allocationProblem(rows, recognizable, t), [rows, recognizable, t]);
  const totals = allocationTotals(rows);
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text type="secondary">{t('allocationOptionalHint')}</Text>
      <Table<AllocationRow>
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={rows}
        scroll={{ x: 1300 }}
        locale={{ emptyText: t('noAllocationFullExpense') }}
        columns={[
          {
            title: t('basis'),
            key: 'basis',
            width: 140,
            render: (_, r) => (
              <Select
                disabled={disabled}
                style={{ width: '100%' }}
                value={r.allocation_basis}
                options={[
                  { value: 'PERCENTAGE', label: t('percentage') },
                  { value: 'FIXED_AMOUNT', label: t('fixedAmount') },
                ]}
                onChange={(v) => update(r.key, { allocation_basis: v, percentage: null, fixed_amount: null })}
              />
            ),
          },
          {
            title: t('pctOrAmount'),
            key: 'value',
            width: 130,
            render: (_, r) =>
              r.allocation_basis === 'PERCENTAGE' ? (
                <InputNumber
                  disabled={disabled}
                  min={0}
                  max={100}
                  precision={4}
                  addonAfter="%"
                  style={{ width: '100%' }}
                  value={r.percentage == null ? null : Number(r.percentage)}
                  onChange={(v) => update(r.key, { percentage: v == null ? null : String(v) })}
                />
              ) : (
                <InputNumber<string>
                  disabled={disabled}
                  min="0"
                  precision={2}
                  stringMode
                  style={{ width: '100%' }}
                  value={r.fixed_amount == null ? null : String(r.fixed_amount)}
                  onChange={(v) => update(r.key, { fixed_amount: v == null ? null : String(v) })}
                />
              ),
          },
          {
            title: t('expenseAccount'),
            key: 'exp',
            width: 220,
            render: (_, r) => (
              <Select
                disabled={disabled}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t('contractDefault')}
                style={{ width: '100%' }}
                value={r.expense_account_id || undefined}
                options={expenseOptions}
                onChange={(v) => update(r.key, { expense_account_id: v ?? null })}
              />
            ),
          },
          {
            title: t('costCentre'),
            key: 'cc',
            width: 130,
            render: (_, r) => (
              <Input disabled={disabled} value={r.cost_center ?? ''} onChange={(e) => update(r.key, { cost_center: e.target.value || null })} />
            ),
          },
          {
            title: t('project'),
            key: 'prj',
            width: 200,
            render: (_, r) => (
              <Select
                disabled={disabled}
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={r.project_id || undefined}
                options={projectOptions}
                onChange={(v) => update(r.key, { project_id: v ?? null })}
              />
            ),
          },
          {
            title: t('department'),
            key: 'dept',
            width: 180,
            render: (_, r) => (
              <Select
                disabled={disabled}
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={r.department_id || undefined}
                options={departmentOptions}
                onChange={(v) => update(r.key, { department_id: v ?? null })}
              />
            ),
          },
          {
            title: t('effectiveFrom'),
            key: 'from',
            width: 140,
            render: (_, r) => <DatePicker disabled={disabled} value={toDate(r.effective_from)} onChange={(d) => update(r.key, { effective_from: fromDate(d) })} />,
          },
          {
            title: t('effectiveTo'),
            key: 'to',
            width: 140,
            render: (_, r) => <DatePicker disabled={disabled} value={toDate(r.effective_to)} onChange={(d) => update(r.key, { effective_to: fromDate(d) })} />,
          },
          {
            title: '',
            key: 'del',
            width: 48,
            render: (_, r) =>
              disabled ? null : <Button type="link" danger icon={<DeleteOutlined />} onClick={() => onChange(rows.filter((x) => x.key !== r.key))} />,
          },
        ]}
      />
      <Space>
        {!disabled && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() =>
              onChange([...rows, { key: String(Date.now()), allocation_basis: rows[0]?.allocation_basis ?? 'PERCENTAGE', percentage: null, fixed_amount: null }])
            }
          >
            {t('addAllocation')}
          </Button>
        )}
        {rows.length > 0 && (
          <Text type="secondary">
            {totals.pctCount ? t('totalPct', { pct: totals.pct }) : t('totalAmount', { amount: amount(totals.fixed) })}
          </Text>
        )}
      </Space>
      {problem && <Alert type="warning" showIcon message={problem} />}
    </Space>
  );
};

export type CustomLineRow = CustomScheduleLineInput & { key: string };

export const CustomScheduleEditor: React.FC<{
  rows: CustomLineRow[];
  onChange: (rows: CustomLineRow[]) => void;
  target?: string | number | null;
}> = ({ rows, onChange, target }) => {
  const { t } = useTranslation('finance');
  const update = (key: string, patch: Partial<CustomLineRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const total = rows.reduce((s, r) => s + cents(r.amount), 0) / 100;
  const diff = target != null ? Math.round(cents(target) - total * 100) / 100 : 0;
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Table<CustomLineRow>
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={rows}
        locale={{ emptyText: t('addCustomRecognitionLines') }}
        columns={[
          {
            title: t('from'),
            key: 'from',
            render: (_, r) => <DatePicker value={toDate(r.from)} onChange={(d) => update(r.key, { from: fromDate(d) ?? '' })} />,
          },
          {
            title: t('to'),
            key: 'to',
            render: (_, r) => <DatePicker value={toDate(r.to)} onChange={(d) => update(r.key, { to: fromDate(d) ?? '' })} />,
          },
          {
            title: t('amount'),
            key: 'amt',
            render: (_, r) => (
              <InputNumber<string>
                min="0"
                precision={2}
                stringMode
                style={{ width: 160 }}
                value={r.amount === '' ? null : String(r.amount)}
                onChange={(v) => update(r.key, { amount: v == null ? '' : String(v) })}
              />
            ),
          },
          {
            title: '',
            key: 'del',
            width: 48,
            render: (_, r) => <Button type="link" danger icon={<DeleteOutlined />} onClick={() => onChange(rows.filter((x) => x.key !== r.key))} />,
          },
        ]}
      />
      <Space>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            const last = rows[rows.length - 1];
            const from = last?.to ? dayjs(last.to).add(1, 'day') : null;
            onChange([
              ...rows,
              {
                key: String(Date.now()),
                from: from ? from.format('YYYY-MM-DD') : '',
                to: from ? from.endOf('month').format('YYYY-MM-DD') : '',
                amount: '',
              },
            ]);
          }}
        >
          {t('addLine')}
        </Button>
        <Text type={diff !== 0 ? 'warning' : 'secondary'}>
          {t('total')} {amount(total)}
          {target != null && diff !== 0
            ? ` — ${amount(Math.abs(diff))} ${diff > 0 ? t('stillToAllocate') : t('overTheAmount')}`
            : ''}
        </Text>
      </Space>
    </Space>
  );
};

export const SchedulePreviewTable: React.FC<{ preview: SchedulePreview | null | undefined; currency?: string | null; loading?: boolean }> = ({
  preview,
  currency,
  loading,
}) => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  if (!preview && !loading) return <Text type="secondary">{t('usePreviewScheduleHint')}</Text>;
  const showBase = preview && currency && preview.base_currency && preview.base_currency !== currency;
  return (
    <Table
      size="small"
      rowKey={(r) => `${r.periodKey}-${r.from}`}
      loading={loading}
      dataSource={preview?.lines ?? []}
      pagination={(preview?.lines.length ?? 0) > 24 ? { pageSize: 24 } : false}
      columns={[
        { title: t('period'), dataIndex: 'periodKey', width: 90 },
        { title: t('from'), dataIndex: 'from', width: 110 },
        { title: t('to'), dataIndex: 'to', width: 110 },
        { title: t('days'), dataIndex: 'recognitionDays', width: 70, align: 'right' },
        { title: `Amount${currency ? ` (${currency})` : ''}`, dataIndex: 'amount', align: 'right', render: (v: string) => amount(v) },
        ...(showBase
          ? [{ title: `Base (${preview?.base_currency})`, dataIndex: 'baseAmount', align: 'right' as const, render: (v: string) => amount(v) }]
          : []),
        { title: t('postingDate'), dataIndex: 'postingDate', width: 120 },
      ]}
      summary={() =>
        preview ? (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <Text strong>Total ({preview.lines.length} lines)</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              {preview.totalRecognitionDays}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              <Text strong>{amount(preview.totalAmount)}</Text>
            </Table.Summary.Cell>
            {showBase ? (
              <Table.Summary.Cell index={5} align="right">
                <Text strong>{amount(preview.totalBaseAmount)}</Text>
              </Table.Summary.Cell>
            ) : null}
            <Table.Summary.Cell index={showBase ? 6 : 5} />
          </Table.Summary.Row>
        ) : null
      }
    />
  );
};
